import { memo, useState } from 'react'

import { Load } from '@/entities/schedules/models/Load'
import { Icon } from '@/shared/components'
import { Dropdown } from '@/shared/components/Dropdown/Dropdown'
import { DropdownItem } from '@/shared/components/Dropdown/DropdownItem'
import { TREE_WIDGETS } from '@/shared/components/Mosaic/models/widgetType'
import { pxToRem } from '@/shared/styles/themes'
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { Badge, Box, Divider, styled, Tooltip, Typography } from '@mui/material'
import { TreeItem2Label } from '@mui/x-tree-view/TreeItem2'

import { getShiftIconFromStatus } from './lib/getShiftIconFromStatus'
import { CustomTreeItemType } from './model/CustomTreeItemType'
import { TreeLabelUtilisationPie } from './TreeLabelUtilisationPie'

export type TreeLabelProps = {
  children: React.ReactNode | null
  icon?: React.ReactNode
  expandable?: boolean
  treeItem: CustomTreeItemType
}

export const TreeLabel: React.FC<TreeLabelProps> = memo(({
  icon: ItemIcon,
  expandable,
  children,
  treeItem,
  ...props
}) => {
  const [isActionVisible, setIsActionVisible] = useState(false)
  const { actions, violationCount, widgetType } = treeItem

  const onDropdownOpenChange = (open: boolean) => {
    setIsActionVisible(open)
  }

  const isShiftWithLoads = (
    widgetType === TREE_WIDGETS.PlanningShiftTable ||
    widgetType === TREE_WIDGETS.RunsheetShiftTable
  ) && 'loads' in treeItem && treeItem.loads.length > 1

  const renderUtilisationPies = (load?: Load) => {
    if (widgetType !== TREE_WIDGETS.PlanningShiftTable && widgetType !== TREE_WIDGETS.RunsheetShiftTable)
      return null

    if (!treeItem.showUtilisationPie)
      return null

    // If load is provided, use load's values, otherwise use shift's total values
    const values = load ? {
      maxWeight: load.maxWeight,
      maxVolume: load.maxVolume,
      totalVolume: load.totalVolume,
      totalWeight: load.totalWeight,

      weightCapacity: treeItem.weightCapacity,
      volumeCapacity: treeItem.volumeCapacity,
      totalWorkingTimeInMins: treeItem.totalWorkingTimeInMins,
      maxWorkingTimeInMins: treeItem.maxWorkingTimeInMins,
    } : {
      maxWeight: treeItem.maxWeight,
      maxVolume: treeItem.maxVolume,
      weightCapacity: treeItem.weightCapacity,
      volumeCapacity: treeItem.volumeCapacity,
      totalWorkingTimeInMins: treeItem.totalWorkingTimeInMins,
      maxWorkingTimeInMins: treeItem.maxWorkingTimeInMins,
    }

    return (
      <StyledUtilisationPiesContainer>
        {isShiftWithLoads ? (
          <></>
        ) : (
          <>
            <TreeLabelUtilisationPie
              type={'weight'}
              maxValue={values.maxWeight}
              capacity={values.weightCapacity}
            />
            <TreeLabelUtilisationPie
              type={'volume'}
              maxValue={values.maxVolume}
              capacity={values.volumeCapacity}
            />
          </>
        )}
        {load ? (
          <>
            <TreeLabelUtilisationPie
              type={'weight'}
              maxValue={values.maxWeight}
              capacity={values.weightCapacity}
            />
            <TreeLabelUtilisationPie
              type={'volume'}
              maxValue={values.maxVolume}
              capacity={values.volumeCapacity}
            />
            <Box sx={{ width: pxToRem(21), height: pxToRem(21) }} />
          </>
        ) : (
          <TreeLabelUtilisationPie
            type={'workingHours'}
            maxValue={values.totalWorkingTimeInMins}
            capacity={values.maxWorkingTimeInMins}
          />
        )}
      </StyledUtilisationPiesContainer>
    )
  }

  let runsheetStatusIcon = null

  if (widgetType && widgetType === TREE_WIDGETS.RunsheetShiftTable) {
    runsheetStatusIcon = getShiftIconFromStatus(treeItem.status, treeItem.statusTooltip)
  }

  return (
    <TreeItem2Label
      {...props}
      sx={{
        display: 'flex',
        direction: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        overflow: 'visible',
      }}
    >
      <StyledLabel
        sx={{
          paddingLeft: `${treeItem.level * 12}px`,
        }}
      >
        <StyledMainRow>
          <StyledLabelContent>
            {ItemIcon}
            <StyledTreeItemLabelText variant="body1" noWrap>{children}</StyledTreeItemLabelText>
            {violationCount ? (violationCount > 0 && (
              <Tooltip title={`${violationCount} violations`} placement="bottom" arrow>
                <StyledViolationBadge
                  badgeContent={violationCount > 0 ? violationCount : ''}
                  color="error"
                  max={99}
                >
                  <StyledViolationIcon icon={faTriangleExclamation} />
                </StyledViolationBadge>
              </Tooltip>
            )) : null}
            {runsheetStatusIcon}
          </StyledLabelContent>
          {(widgetType === TREE_WIDGETS.PlanningShiftTable ||
            widgetType === TREE_WIDGETS.RunsheetShiftTable) &&
            renderUtilisationPies()
          }
        </StyledMainRow>
        {isShiftWithLoads && (
          <StyledLoadsContainer>
            {treeItem.loads.map((load, index) => (
              <StyledLoadRow key={index}>
                <StyledLoadText variant="caption">
                  Load {index + 1}
                </StyledLoadText>
                {renderUtilisationPies(load)}
              </StyledLoadRow>
            ))}
          </StyledLoadsContainer>
        )}
      </StyledLabel>
      <StyledActionsContainer className="menu-item-action" $isActionVisible={isActionVisible}>
        {actions && actions.length > 0 && <Dropdown
          icon={<StyledActionIcon icon={faEllipsisVertical} />}
          value="Actions"
          isIconButton
          onOpenChange={onDropdownOpenChange}
          slotProps={{
            iconButton: {
              round: true,
              sx: {
                margin: 0,
              },
            },
            popper: {
              placement: 'right-start',
            },
          }}
        >
          {actions.map((actionItem, index) => actionItem.type === 'actionItem' ? (
            <DropdownItem
              key={index}
              onSelect={actionItem.action}
              disabled={actionItem.disabled}
            >
              <Icon icon={actionItem.icon} />
              {actionItem.label}
            </DropdownItem>
          ) : (
            <li key={index}>
              <Divider />
            </li>
          ))}
        </Dropdown>}
      </StyledActionsContainer>
    </TreeItem2Label>
  )
}, (prevProps, nextProps) => {
  return (
    prevProps.treeItem.id == nextProps.treeItem.id &&
    prevProps.treeItem.violationCount == nextProps.treeItem.violationCount &&
    prevProps.treeItem.widgetType == nextProps.treeItem.widgetType &&
    prevProps.treeItem.label === nextProps.treeItem.widgetType &&
    prevProps.children === nextProps.children
  )
})

const StyledLabel = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-grow: 1;
`

const StyledMainRow = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`

const StyledLabelContent = styled('div')`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  max-width: 100%;
`

const StyledTreeItemLabelText = styled(Typography)`
  color: inherit;
  font-size: ${props => props.theme.typography.pxToRem(14)};
  max-width: ${props => props.theme.typography.pxToRem(200)};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const StyledLoadsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-left: ${props => props.theme.typography.pxToRem(24)};
  gap: 2px;
`

const StyledLoadRow = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const StyledLoadText = styled(Typography)`
  color: ${props => props.theme.palette.text.secondary};
  font-size: ${props => props.theme.typography.pxToRem(14)};
`

const StyledViolationBadge = styled(Badge)`
  margin-right: ${props => props.theme.typography.pxToRem(2)};

  .MuiBadge-badge {
    background-color: transparent;
    color: ${props => props.theme.palette.error.main};
    font-size: ${props => props.theme.typography.pxToRem(12)};
    top: 2px;
    font-style: italic;
  }
`

const StyledViolationIcon = styled(Icon)`
  color: ${props => props.theme.palette.error.main};
  font-size: ${props => props.theme.typography.pxToRem(14)};
`

const StyledUtilisationPiesContainer = styled(Box)`
  height: 100%;
  display: flex;
  gap: 2px;
  align-items: center;
  justify-content: flex-end;
  margin-right: ${pxToRem(24)};
`

const StyledActionsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isActionVisible',
}) <{ $isActionVisible: boolean }>`
  position: absolute;
  right: -8px;
  top: -5px;
  display: inline-flex;
  flex-direction: row;
  opacity: 0;

  ${props => props.$isActionVisible && `
    opacity: 1;
  `}
`

const StyledActionIcon = styled(Icon)`
  font-size: ${props => props.theme.typography.pxToRem(16)};
  width: ${props => props.theme.typography.pxToRem(16)};
  height: ${props => props.theme.typography.pxToRem(16)};
  transform: none !important;
`